/**
 * İlan alanlarını filtreleme ve kayıt için standart formata dönüştürür.
 * Sahibinden ZIP import, admin formu ve frontend filtreleri aynı mantığı kullanır.
 */

const PROPERTY_TYPE_ALIASES = {
    daire: 'daire',
    apartment: 'daire',
    'satılık_daire': 'daire',
    'satilik_daire': 'daire',
    villa: 'villa',
    residence: 'residence',
    müstakil: 'müstakil',
    mustakil: 'müstakil',
    müstakil_ev: 'müstakil',
    mustakil_ev: 'müstakil',
    dublex: 'dublex',
    triplex: 'triplex',
    çiftlik: 'çiftlik',
    ciftlik: 'çiftlik',
    yazlık: 'yazlık',
    yazlik: 'yazlık',
    ofis: 'ofis',
    office: 'ofis',
    işyeri: 'işyeri',
    isyeri: 'işyeri',
    'iş_yeri': 'işyeri',
    dükkan: 'dukkan',
    dukkan: 'dukkan',
    shop: 'dukkan',
    arsa: 'arsa',
    land: 'arsa'
};

const SAHIBINDEN_PROPERTY_TYPE_MAP = {
    'Satılık Daire': 'daire',
    'Kiralık Daire': 'daire',
    'Satılık Villa': 'villa',
    'Kiralık Villa': 'villa',
    'Satılık Müstakil Ev': 'müstakil',
    'Satılık Ofis': 'ofis',
    'Satılık Dükkan': 'dukkan',
    'Satılık Arsa': 'arsa',
    'Satılık İş Yeri': 'işyeri',
    'Kiralık İş Yeri': 'işyeri'
};

export function normalizeRoomCount(value) {
    if (value == null || value === '') return null;

    const str = String(value)
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '')
        .replace(/(oda|daire|room).*/i, '');

    const plusMatch = str.match(/^(\d+)\+(\d+)/);
    if (plusMatch) {
        const rooms = parseInt(plusMatch[1], 10);
        return Number.isFinite(rooms) ? rooms : null;
    }

    const num = parseInt(str.replace(/[^\d]/g, ''), 10);
    if (Number.isFinite(num) && num > 0 && num < 20) {
        return num;
    }

    return null;
}

export function formatRoomCountForStorage(value) {
    const normalized = normalizeRoomCount(value);
    return normalized != null ? String(normalized) : '';
}

export function normalizeRoomCountFilterValue(value) {
    if (value == null || value === '' || value === 'all') return 'all';
    if (String(value).trim() === '4+') return '4+';

    const normalized = normalizeRoomCount(value);
    return normalized != null ? String(normalized) : 'all';
}

export function formatRoomCountLabel(value) {
    const normalized = normalizeRoomCount(value);
    if (normalized == null) return '—';
    return `${normalized}+1`;
}

export function matchesRoomCount(propertyRooms, filterValue) {
    if (!filterValue || filterValue === 'all') return true;

    const roomCount = normalizeRoomCount(propertyRooms);
    if (roomCount == null) return false;

    const normalizedFilter = normalizeRoomCountFilterValue(filterValue);
    if (normalizedFilter === 'all') return true;

    if (normalizedFilter === '4+') {
        return roomCount >= 4;
    }

    const filterNum = parseInt(normalizedFilter, 10);
    if (!Number.isFinite(filterNum)) return true;

    return roomCount === filterNum;
}

export function normalizePropertyType(type) {
    if (!type) return '';

    const raw = String(type).trim();
    if (SAHIBINDEN_PROPERTY_TYPE_MAP[raw]) {
        return SAHIBINDEN_PROPERTY_TYPE_MAP[raw];
    }

    const key = raw
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9çğıöşü_]/gi, '');

    return PROPERTY_TYPE_ALIASES[key] || key;
}

export function matchesPropertyType(property, filterValue) {
    if (!filterValue || filterValue === 'all') return true;

    const propertyType = normalizePropertyType(property.propertyType || property.type);
    const filterType = normalizePropertyType(filterValue);

    if (propertyType === filterType) return true;

    // Ofis / işyeri çapraz eşleşme
    if (
        (filterType === 'ofis' || filterType === 'işyeri')
        && (propertyType === 'ofis' || propertyType === 'işyeri')
    ) {
        return true;
    }

    return false;
}

export function normalizeFloor(floor) {
    if (floor == null || floor === '') return null;

    const str = String(floor).toLowerCase().trim();
    if (
        str === '0'
        || str.includes('zemin')
        || str.includes('giriş')
        || str.includes('giris')
        || str.includes('bahçe')
        || str.includes('bahce')
    ) {
        return 0;
    }

    const num = parseInt(str.replace(/[^\d-]/g, ''), 10);
    return Number.isFinite(num) ? num : null;
}

export function formatFloorForStorage(floor) {
    const normalized = normalizeFloor(floor);
    return normalized != null ? normalized : '';
}

export function matchesFloor(propertyFloor, filterValue) {
    if (!filterValue || filterValue === 'all') return true;

    const floor = normalizeFloor(propertyFloor);
    if (floor == null) return false;

    switch (filterValue) {
        case 'ground':
            return floor === 0;
        case '1-3':
            return floor >= 1 && floor <= 3;
        case '4-7':
            return floor >= 4 && floor <= 7;
        case '8+':
            return floor >= 8;
        default:
            return true;
    }
}

export function normalizeBuildingAge(value) {
    if (value == null || value === '') return null;

    const str = String(value).trim().toLowerCase();
    const currentYear = new Date().getFullYear();

    if (str.includes('0') && (str.includes('yeni') || str.includes('sıfır') || str === '0')) {
        return 0;
    }

    const rangeMatch = str.match(/(\d+)\s*[-–]\s*(\d+)/);
    if (rangeMatch) {
        const low = parseInt(rangeMatch[1], 10);
        const high = parseInt(rangeMatch[2], 10);
        if (Number.isFinite(low) && Number.isFinite(high)) {
            return Math.round((low + high) / 2);
        }
    }

    const num = parseInt(str.replace(/[^\d]/g, ''), 10);
    if (!Number.isFinite(num)) return null;

    if (num > 1900 && num <= currentYear) {
        return currentYear - num;
    }

    return num;
}

export function formatBuildingAgeForStorage(value) {
    const normalized = normalizeBuildingAge(value);
    return normalized != null ? normalized : '';
}

export function matchesBuildingAge(buildingAge, filterRange) {
    if (!filterRange || filterRange === 'all') return true;

    const age = normalizeBuildingAge(buildingAge);
    if (age == null) return false;

    switch (filterRange) {
        case '0-5':
            return age <= 5;
        case '6-10':
            return age >= 6 && age <= 10;
        case '11-20':
            return age >= 11 && age <= 20;
        case '20+':
            return age > 20;
        default:
            return true;
    }
}

export function normalizeHeating(heating) {
    if (!heating) return '';

    const h = String(heating).toLowerCase().trim();
    if (h.includes('merkezi')) return 'merkezi';
    if (h.includes('kombi')) return 'kombi';
    if (h.includes('klima')) return 'klima';
    if (h.includes('soba')) return 'soba';
    if (h.includes('şömine') || h.includes('somine')) return 'şömine';
    if (h.includes('yerden')) return 'yerden';
    if (h.includes('yok') || h === 'yok') return 'yok';

    return h;
}

export function matchesHeating(propertyHeating, filterValue) {
    if (!filterValue || filterValue === 'all') return true;
    return normalizeHeating(propertyHeating) === normalizeHeating(filterValue);
}

export function getPropertySize(property) {
    const candidates = [property?.size, property?.grossSize, property?.netSize];
    for (const candidate of candidates) {
        if (candidate == null || candidate === '') continue;
        const num = parseInt(String(candidate).replace(/[^\d]/g, ''), 10);
        if (Number.isFinite(num) && num > 0) return num;
    }
    return 0;
}

export function mapSahibindenPropertyType(emlakTipi) {
    if (!emlakTipi) return 'daire';
    if (SAHIBINDEN_PROPERTY_TYPE_MAP[emlakTipi]) {
        return SAHIBINDEN_PROPERTY_TYPE_MAP[emlakTipi];
    }
    return normalizePropertyType(emlakTipi) || 'daire';
}

export function mapSahibindenBuildingAge(binaYasi) {
    return formatBuildingAgeForStorage(binaYasi);
}

export function mapSahibindenHeating(isitma) {
    return normalizeHeating(isitma) || 'merkezi';
}
